import React, { useEffect, useState } from "react";
import {
  Users,
  UserPlus,
  Shield,
  Home,
  Mail,
  Key,
  Activity,
  Bell,
  Search,
  Filter,
  BarChart3,
  TrendingUp,
  AlertCircle,
  Check,
  X,
  Pencil,
  UserX,
  UserCheck,
  RefreshCw,
  MapPin,
  Settings,
} from "lucide-react";
import api from "../api/axios";
import Header from "./Header";
import VisitSummary from "./VisitSummary";
import SystemConfig from "./SystemConfig";
import { toast } from "react-toastify";
import socketManager from "../api/socket";
import { Card } from "./shared/Card";
import { Button } from "./shared/Button";
import { Badge } from "./shared/Badge";
import { Table } from "./shared/Table";
import Navegador from "./modulos/Navegador";
import { coincide } from "../utils/texto";
import useAuthStore from "../store/useAuthStore";

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "var(--bgSecondary)",
  },
  content: {
    maxWidth: "var(--container-max-width)",
    margin: "0 auto",
    padding: "var(--space-8) var(--container-padding)",
  },
  header: {
    marginBottom: "var(--space-8)",
  },
  title: {
    fontSize: "var(--text-3xl)",
    fontWeight: "600",
    color: "var(--text)",
    marginBottom: "var(--space-2)",
  },
  subtitle: {
    fontSize: "var(--text-base)",
    color: "var(--textSecondary)",
    lineHeight: "1.5",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(250px, 100%), 1fr))",
    gap: "var(--space-4)",
    marginBottom: "var(--space-8)",
  },
  statCard: {
    padding: "var(--space-5) var(--space-4)",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-4)",
  },
  statIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: "var(--text-2xl)",
    fontWeight: "600",
    color: "var(--text)",
  },
  statLabel: {
    fontSize: "var(--text-sm)",
    color: "var(--textSecondary)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "var(--space-6)",
    marginBottom: "var(--space-8)",
  },
  gridDesktop: {
    "@media (min-width: 768px)": {
      gridTemplateColumns: "1fr 1fr",
      gap: "var(--space-8)",
    },
  },
  form: {
    width: "100%",
  },
  inputGroup: {
    marginBottom: "var(--space-5)",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    fontSize: "var(--text-sm)",
    fontWeight: "500",
    color: "var(--text)",
    marginBottom: "var(--space-2)",
  },
  inputWrapper: {
    position: "relative",
  },
  inputIcon: {
    position: "absolute",
    left: "0.75rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--textTertiary)",
  },
  input: {
    width: "100%",
    padding: "var(--space-3) var(--space-4) var(--space-3) 2.5rem",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg)",
    fontSize: "var(--text-sm)",
    color: "var(--text)",
    transition: "border-color 0.2s",
    minHeight: "44px",
  },
  inputFocus: {
    borderColor: "var(--primary)",
    outline: "none",
  },
  select: {
    width: "100%",
    padding: "var(--space-3) var(--space-4) var(--space-3) 2.5rem",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg)",
    fontSize: "var(--text-sm)",
    color: "var(--text)",
    cursor: "pointer",
    appearance: "none",
    minHeight: "44px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "var(--space-4)",
  },
  rowDesktop: {
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  message: {
    padding: "var(--space-3) var(--space-4)",
    borderRadius: "var(--radius-md)",
    fontSize: "var(--text-sm)",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    marginBottom: "var(--space-4)",
  },
  successMessage: {
    backgroundColor: "var(--successBg)",
    color: "var(--successText)",
  },
  errorMessage: {
    backgroundColor: "var(--dangerBg)",
    color: "var(--dangerText)",
  },
  actions: {
    display: "flex",
    gap: "var(--space-3)",
    justifyContent: "flex-end",
    marginTop: "var(--space-6)",
    flexWrap: "wrap",
  },
  searchContainer: {
    position: "relative",
    marginBottom: "var(--space-4)",
  },
  searchIcon: {
    position: "absolute",
    left: "var(--space-3)",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-tertiary)",
  },
  searchInput: {
    width: "100%",
    padding: "var(--space-3) var(--space-4)",
    paddingLeft: "2.5rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-base)",
    color: "var(--text)",
    transition: "all 0.2s ease",
    outline: "none",
    minHeight: "44px",
  },
  searchInputFocus: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--bg-secondary)",
    boxShadow: "0 0 0 4px var(--gold-subtle)",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "var(--space-4)",
    flexWrap: "wrap",
    gap: "var(--space-3)",
  },
  sectionTitle: {
    fontSize: "var(--text-xl)",
    fontWeight: "600",
    color: "var(--text)",
  },
  notificationCard: {
    position: "fixed",
    bottom: "var(--space-4)",
    right: "var(--space-4)",
    left: "var(--space-4)",
    maxWidth: "350px",
    marginLeft: "auto",
    padding: "var(--space-4)",
    backgroundColor: "var(--bg)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-lg)",
    border: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-4)",
    zIndex: 1000,
  },
  notificationIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--primaryBg)",
    color: "var(--primary)",
  },
  tabs: {
    display: "flex",
    gap: "var(--space-2)",
    marginBottom: "var(--space-8)",
    borderBottom: "2px solid var(--border)",
  },
  tab: {
    padding: "var(--space-3) var(--space-6)",
    fontSize: "var(--text-base)",
    fontWeight: "600",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "var(--textSecondary)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    marginBottom: "-2px",
  },
  tabActive: {
    color: "var(--primary)",
    borderBottomColor: "var(--primary)",
  },
};

export default function AdminDashboard() {
  // Para saber cuál de las filas es uno mismo.
  const { user: usuarioSesion } = useAuthStore();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "resident",
    houseNumber: "",
    polygon: "",
  });
  const [message, setMessage] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [showVisitSummary, setShowVisitSummary] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("users"); // "users" or "config"
  const [stats, setStats] = useState({
    totalUsers: 0,
    residents: 0,
    guards: 0,
    todayActivity: 0,
  });
  /* Si la carga falla hay que DECIRLO. Antes el `catch` solo hacía
     `console.error` y vaciaba la lista, así que la pantalla mostraba «No hay
     usuarios registrados aún» — que es una respuesta distinta de «no se pudo
     cargar», y en una administración esa diferencia importa. */
  const [usersError, setUsersError] = useState(null);
  /* Edición y baja de usuarios. El servidor ya las tenía
     (`PUT /users/:id`, `DELETE /users/:id`) y la tabla no las ofrecía: cuando
     un residente se mudaba o se despedía a un guardia, **la administración no
     tenía forma de revocarle el acceso**, en un sistema cuyo trabajo es
     justamente controlar quién entra. */
  const [editando, setEditando] = useState(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const visitSummaryRef = React.useRef(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const fetchUsers = async () => {
    try {
      setUsersError(null);
      const res = await api.get("/users");
      const userList = res.data.users || res.data || [];
      setUsers(userList);

      const newStats = userList.reduce(
        (acc, user) => {
          acc.totalUsers++;
          if (user.role === "resident") acc.residents++;
          else if (user.role === "guard") acc.guards++;
          return acc;
        },
        { totalUsers: 0, residents: 0, guards: 0 }
      );

      setStats((prev) => ({ ...prev, ...newStats }));
    } catch (err) {
      setUsers([]);
      setUsersError(
        err?.response?.data?.message ||
          "No se pudieron cargar los usuarios. Reintente."
      );
    }
  };

  /**
   * Las entradas de hoy, del servidor.
   *
   * Antes `todayActivity` arrancaba en 0 y solo subía al recibir un evento con
   * la página abierta: era un contador de sesión con etiqueta de total diario,
   * y al cargar la pantalla decía 0 siempre, aunque hubieran entrado veinte
   * visitas. Ahora la cifra sale del panel y los eventos en vivo la suben
   * encima.
   */
  const fetchActividad = async () => {
    try {
      const res = await api.get("/panel/seguridad");
      // `GET /panel/seguridad` responde `hoy: { entradas, salidas }`.
      const entradas = Number(res.data?.hoy?.entradas ?? 0);
      setStats((prev) => ({
        ...prev,
        todayActivity: Number.isFinite(entradas) ? entradas : 0,
      }));
    } catch {
      /* Silencioso a propósito: es una cifra de apoyo, no el contenido de la
         pantalla. Que falle no debe tapar la lista de usuarios con un aviso. */
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchActividad();

    const socket = socketManager.get();
    socket.emit("joinAdmin");

    socket.on("visitCreated", (visit) => {
      // Don't show toast here - NotificationBell component will handle it
      setStats((prev) => ({ ...prev, todayActivity: prev.todayActivity + 1 }));
    });

    socket.on("visitUpdated", (visit) => {
      // Don't show toast here - NotificationBell component will handle it
    });

    return () => {
      socket.off("visitCreated");
      socket.off("visitUpdated");
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      const payload = { ...form };
      if (form.role !== "resident") {
        delete payload.houseNumber;
        delete payload.polygon;
      }
      await api.post("/auth/register", payload);
      setMessage({ type: "success", text: "Usuario creado correctamente" });
      setForm({
        name: "",
        email: "",
        password: "",
        role: "resident",
        houseNumber: "",
        polygon: "",
      });
      fetchUsers();
    } catch (err) {
      setMessage({ type: "error", text: "Error al crear usuario" });
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicion = (u) =>
    setEditando({
      _id: u._id,
      name: u.name || "",
      email: u.email || "",
      role: u.role,
      houseNumber: u.houseNumber || "",
      polygon: u.polygon || "",
    });

  const guardarEdicion = async (e) => {
    e.preventDefault();
    setGuardandoEdicion(true);
    try {
      const cuerpo = {
        name: editando.name,
        email: editando.email,
        role: editando.role,
      };
      // La casa y el polígono solo significan algo para un residente.
      if (editando.role === "resident") {
        cuerpo.houseNumber = editando.houseNumber;
        cuerpo.polygon = editando.polygon;
      }
      await api.put(`/users/${editando._id}`, cuerpo);
      toast.success("Usuario actualizado");
      setEditando(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "No se pudo actualizar el usuario");
    } finally {
      setGuardandoEdicion(false);
    }
  };

  /**
   * Dar de baja NO borra: el servidor pone `isActive` en falso.
   *
   * Es lo correcto para un control de acceso —el historial de quién entró
   * tiene que seguir existiendo— y por eso el rótulo dice «Dar de baja» y no
   * «Eliminar»: prometer un borrado que no ocurre es peor que no ofrecerlo.
   */
  const cambiarEstado = async (u) => {
    const dando = u.isActive !== false;
    const aviso = dando
      ? `¿Dar de baja a ${u.name}? Dejará de poder entrar al sistema; sus datos y su historial se conservan.`
      : `¿Reactivar a ${u.name}?`;
    if (!window.confirm(aviso)) return;
    try {
      if (dando) {
        await api.delete(`/users/${u._id}`);
        toast.success(`${u.name} quedó dado de baja`);
      } else {
        await api.put(`/users/${u._id}`, { isActive: true });
        toast.success(`${u.name} quedó reactivado`);
      }
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "No se pudo cambiar el estado");
    }
  };

  /* `coincide` en vez de `toLowerCase().includes()`: quita tildes y recorta
     espacios, igual que el backend. Antes, «Martínez» no encontraba a
     «Martinez» —y al revés—, y `  juan  ` no encontraba nada. Además tolera
     un campo vacío: `user.name.toLowerCase()` reventaba si el nombre era nulo. */
  const filteredUsers = users.filter(
    (user) =>
      coincide(user.name, searchTerm) ||
      coincide(user.email, searchTerm) ||
      coincide(user.role, searchTerm) ||
      coincide(user.houseNumber, searchTerm) ||
      coincide(user.polygon, searchTerm)
  );

  const columns = [
    {
      key: "name",
      label: "Nombre",
      render: (value) => <div style={{ fontWeight: "500" }}>{value}</div>,
    },
    {
      key: "email",
      label: "Correo Electrónico",
      render: (value) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Mail size={14} style={{ color: "var(--textTertiary)" }} />
          {value}
        </div>
      ),
    },
    {
      key: "role",
      label: "Rol",
      /* Sin `color: "white"` forzado: `Badge` ya elige el color de texto que
         contrasta con cada variante (`--danger-text`, `--warning-text`…). El
         blanco de antes lo anulaba y dejaba las insignias ilegibles en modo
         claro: sólo se distinguía el ícono. */
      render: (value) => (
        <Badge
          variant={
            value === "admin"
              ? "danger"
              : value === "guard"
              ? "warning"
              : "primary"
          }
          icon={value === "admin" ? "👑" : value === "guard" ? "🛡️" : "🏠"}
        >
          {value === "resident"
            ? "Residente"
            : value === "guard"
            ? "Guardia"
            : "Admin"}
        </Badge>
      ),
    },
    {
      key: "houseNumber",
      label: "Casa",
      render: (value, row) =>
        row.role === "resident" ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Home size={14} style={{ color: "var(--textTertiary)" }} />
            {value}
          </div>
        ) : (
          <span style={{ color: "var(--textTertiary)" }}>-</span>
        ),
    },
    {
      key: "polygon",
      label: "Polígono",
      render: (value, row) =>
        row.role === "resident" ? (
          value
        ) : (
          <span style={{ color: "var(--textTertiary)" }}>-</span>
        ),
    },
    {
      key: "isActive",
      label: "Estado",
      render: (value) =>
        value === false ? (
          <Badge variant="default">De baja</Badge>
        ) : (
          <Badge variant="success">Activo</Badge>
        ),
    },
    {
      key: "_id",
      label: "Acciones",
      render: (value, row) => {
        /* Uno no se edita ni se da de baja a sí mismo: el servidor lo rechaza
           («No puedes eliminarte a ti mismo») y ofrecer un botón que siempre
           falla es peor que no ofrecerlo. El superadministrador no aparece
           siquiera en esta lista. */
        const soyYo = String(row._id) === String(usuarioSesion?._id || usuarioSesion?.id);
        const deBaja = row.isActive === false;
        if (soyYo) {
          return <span style={{ color: "var(--textTertiary)" }}>Su cuenta</span>;
        }
        return (
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => abrirEdicion(row)}
              leftIcon={<Pencil size={14} />}
              aria-label={`Editar a ${row.name}`}
            >
              Editar
            </Button>
            <Button
              variant={deBaja ? "ghost" : "danger"}
              size="sm"
              onClick={() => cambiarEstado(row)}
              leftIcon={deBaja ? <UserCheck size={14} /> : <UserX size={14} />}
              aria-label={`${deBaja ? "Reactivar" : "Dar de baja"} a ${row.name}`}
            >
              {deBaja ? "Reactivar" : "Dar de baja"}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Header />
      {/* El `ToastContainer` vive SÓLO en `App.js`.
        *
        * Aquí había un segundo, así que en esta pantalla cada aviso se
        * dibujaba DOS VECES, y con estilos distintos: el de arriba como
        * tarjeta y el de abajo con `theme="colored"`, de donde salía la
        * barra verde que asomaba por debajo de la caja blanca. */}

      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.header}>
            <h1 style={styles.title}>Panel de Administración</h1>
            <p style={styles.subtitle}>
              Gestiona usuarios, monitorea actividad y genera reportes del
              sistema residencial
            </p>
          </div>

          <div style={styles.tabs}>
            <button
              onClick={() => setActiveTab("users")}
              style={{
                ...styles.tab,
                ...(activeTab === "users" ? styles.tabActive : {}),
              }}
            >
              <Users size={18} />
              Usuarios
            </button>
            <button
              onClick={() => setActiveTab("config")}
              style={{
                ...styles.tab,
                ...(activeTab === "config" ? styles.tabActive : {}),
              }}
            >
              <Settings size={18} />
              Configuración
            </button>
          </div>

          {activeTab === "users" ? (
            <>
              <div style={styles.stats}>
            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--primaryBg)",
                  color: "var(--primary)",
                }}
              >
                <Users size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.totalUsers}</div>
                <div style={styles.statLabel}>Total Usuarios</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--successBg)",
                  color: "var(--success)",
                }}
              >
                <Home size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.residents}</div>
                <div style={styles.statLabel}>Residentes</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--warningBg)",
                  color: "var(--warning)",
                }}
              >
                <Shield size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.guards}</div>
                <div style={styles.statLabel}>Guardias</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--infoBg)",
                  color: "var(--info)",
                }}
              >
                <Activity size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.todayActivity}</div>
                <div style={styles.statLabel}>Actividad Hoy</div>
              </div>
            </Card>
          </div>

          <div style={styles.grid} className="md:grid-cols-2">
            <Card
              title="Crear Nuevo Usuario"
              subtitle="Agrega residentes o guardias al sistema"
            >
              <form onSubmit={handleSubmit} style={styles.form}>
                {message && (
                  <div
                    style={{
                      ...styles.message,
                      ...(message.type === "success"
                        ? styles.successMessage
                        : styles.errorMessage),
                    }}
                  >
                    {message.type === "success" ? (
                      <Check size={16} />
                    ) : (
                      <X size={16} />
                    )}
                    {message.text}
                  </div>
                )}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    <Users size={16} />
                    Nombre completo
                  </label>
                  <div style={styles.inputWrapper}>
                    <Users size={18} style={styles.inputIcon} />
                    <input
                      name="name"
                      placeholder="Ingrese nombre completo"
                      value={form.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      required
                      style={{
                        ...styles.input,
                        ...(focusedField === "name" ? styles.inputFocus : {}),
                        ...(isMobile ? { fontSize: '16px' } : {}),
                      }}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    <Mail size={16} />
                    Correo electrónico
                  </label>
                  <div style={styles.inputWrapper}>
                    <Mail size={18} style={styles.inputIcon} />
                    <input
                      name="email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={form.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      required
                      style={{
                        ...styles.input,
                        ...(focusedField === "email" ? styles.inputFocus : {}),
                        ...(isMobile ? { fontSize: '16px' } : {}),
                      }}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    <Key size={16} />
                    Contraseña
                  </label>
                  <div style={styles.inputWrapper}>
                    <Key size={18} style={styles.inputIcon} />
                    <input
                      name="password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={form.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField(null)}
                      required
                      style={{
                        ...styles.input,
                        ...(focusedField === "password"
                          ? styles.inputFocus
                          : {}),
                        ...(isMobile ? { fontSize: '16px' } : {}),
                      }}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    <Shield size={16} />
                    Rol de usuario
                  </label>
                  <div style={styles.inputWrapper}>
                    <Shield size={18} style={styles.inputIcon} />
                    <select
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      style={styles.select}
                    >
                      <option value="resident">Residente</option>
                      <option value="guard">Guardia</option>
                    </select>
                  </div>
                </div>

                {form.role === "resident" && (
                  <div style={styles.row} className="sm:grid-cols-2">
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        <Home size={16} />
                        Número de casa
                      </label>
                      <div style={styles.inputWrapper}>
                        <Home size={18} style={styles.inputIcon} />
                        <input
                          name="houseNumber"
                          placeholder="Ej: 15, 23A"
                          value={form.houseNumber}
                          onChange={handleChange}
                          onFocus={() => setFocusedField("houseNumber")}
                          onBlur={() => setFocusedField(null)}
                          required
                          style={{
                            ...styles.input,
                            ...(focusedField === "houseNumber"
                              ? styles.inputFocus
                              : {}),
                            ...(isMobile ? { fontSize: '16px' } : {}),
                          }}
                        />
                      </div>
                    </div>

                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        <MapPin size={16} />
                        Polígono
                      </label>
                      <div style={styles.inputWrapper}>
                        <MapPin size={18} style={styles.inputIcon} />
                        <input
                          name="polygon"
                          placeholder="Ej: A, B, C"
                          value={form.polygon}
                          onChange={handleChange}
                          onFocus={() => setFocusedField("polygon")}
                          onBlur={() => setFocusedField(null)}
                          required
                          style={{
                            ...styles.input,
                            ...(focusedField === "polygon"
                              ? styles.inputFocus
                              : {}),
                            ...(isMobile ? { fontSize: '16px' } : {}),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div style={styles.actions}>
                  <Button
                    type="submit"
                    loading={loading}
                    disabled={loading}
                    leftIcon={<UserPlus size={16} />}
                  >
                    Crear Usuario
                  </Button>
                </div>
              </form>
            </Card>

            <Card>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "2rem",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "16px",
                    backgroundColor: "var(--primaryBg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <BarChart3 size={40} style={{ color: "var(--primary)" }} />
                </div>
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "600",
                    marginBottom: "0.5rem",
                  }}
                >
                  Análisis y Reportes
                </h3>
                <p
                  style={{
                    color: "var(--textSecondary)",
                    textAlign: "center",
                    marginBottom: "1.5rem",
                  }}
                >
                  Genera reportes detallados de visitas por período, exporta
                  datos y visualiza estadísticas
                </p>
                <Button
                  onClick={() => {
                    const wasHidden = !showVisitSummary;
                    setShowVisitSummary(!showVisitSummary);
                    
                    // If we're showing the summary, scroll to it
                    if (wasHidden) {
                      setTimeout(() => {
                        if (visitSummaryRef.current) {
                          const yOffset = -100; // Negative value to scroll a bit before the element
                          const y = visitSummaryRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          
                          window.scrollTo({
                            top: y,
                            behavior: 'smooth'
                          });
                        }
                      }, 100);
                    }
                  }}
                  leftIcon={<TrendingUp size={16} />}
                  fullWidth
                >
                  {showVisitSummary
                    ? "Ocultar Resumen"
                    : "Ver Resumen de Visitas"}
                </Button>
              </div>
            </Card>
          </div>

          {/* Panel de edición. Aparece sobre la lista al pulsar «Editar» en una
              fila; no es una ventana modal para no tapar la tabla mientras se
              compara con el resto de los usuarios. */}
          {editando && (
            <Card style={{ marginBottom: "var(--space-6)" }}>
              <form onSubmit={guardarEdicion} style={{ padding: "var(--space-2)" }}>
                <h2 style={{ ...styles.sectionTitle, marginBottom: "var(--space-4)" }}>
                  Editar a {editando.name || "este usuario"}
                </h2>

                <div style={styles.grid} className="md:grid-cols-2">
                  <div>
                    <label style={styles.label} htmlFor="editar-nombre">
                      Nombre completo
                    </label>
                    <input
                      id="editar-nombre"
                      style={styles.input}
                      value={editando.name}
                      onChange={(e) => setEditando({ ...editando, name: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label style={styles.label} htmlFor="editar-correo">
                      Correo electrónico
                    </label>
                    <input
                      id="editar-correo"
                      type="email"
                      style={styles.input}
                      value={editando.email}
                      onChange={(e) => setEditando({ ...editando, email: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label style={styles.label} htmlFor="editar-rol">
                      Rol
                    </label>
                    {/* Sin la opción `admin`: el servidor no deja que una
                        administración cree ni ascienda a otra, y ofrecerla
                        sería un botón que siempre falla. */}
                    <select
                      id="editar-rol"
                      style={styles.input}
                      value={editando.role}
                      onChange={(e) => setEditando({ ...editando, role: e.target.value })}
                    >
                      <option value="resident">Residente</option>
                      <option value="guard">Guardia</option>
                    </select>
                  </div>

                  {editando.role === "resident" && (
                    <>
                      <div>
                        <label style={styles.label} htmlFor="editar-casa">
                          Número de casa
                        </label>
                        <input
                          id="editar-casa"
                          style={styles.input}
                          value={editando.houseNumber}
                          onChange={(e) =>
                            setEditando({ ...editando, houseNumber: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div>
                        <label style={styles.label} htmlFor="editar-poligono">
                          Polígono
                        </label>
                        <input
                          id="editar-poligono"
                          style={styles.input}
                          value={editando.polygon}
                          onChange={(e) =>
                            setEditando({ ...editando, polygon: e.target.value })
                          }
                          required
                        />
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-4)" }}>
                  <Button type="submit" loading={guardandoEdicion} leftIcon={<Check size={16} />}>
                    Guardar cambios
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditando(null)}
                    leftIcon={<X size={16} />}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </Card>
          )}

          <Card>
            <div style={styles.tableHeader}>
              <h2 style={styles.sectionTitle}>Usuarios Registrados</h2>
              <Button
                variant="ghost"
                onClick={fetchUsers}
                leftIcon={<Activity size={16} />}
              >
                Actualizar
              </Button>
            </div>

            <div style={styles.searchContainer}>
              <Search size={18} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar por nombre, correo o rol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                style={{
                  ...styles.searchInput,
                  ...(searchFocused ? styles.searchInputFocus : {}),
                  ...(isMobile ? { fontSize: '16px' } : {}),
                }}
              />
            </div>

            {/* El error va ANTES del estado vacío, y corta. «No hay usuarios» y
                «no se pudo cargar» son dos respuestas distintas: la segunda
                pide llamar a alguien, y antes se leían igual. */}
            {usersError ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "var(--space-12) var(--space-4)",
                  color: "var(--textSecondary)",
                }}
              >
                <AlertCircle
                  size={48}
                  style={{ margin: "0 auto var(--space-4)", color: "var(--danger)" }}
                />
                <h3 style={{ marginBottom: "var(--space-2)" }}>
                  No se pudieron cargar los usuarios
                </h3>
                <p style={{ marginBottom: "var(--space-4)" }}>{usersError}</p>
                <Button variant="outline" size="sm" onClick={fetchUsers} leftIcon={<RefreshCw size={14} />}>
                  Reintentar
                </Button>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "var(--space-12) var(--space-4)",
                  color: "var(--textSecondary)",
                }}
              >
                <AlertCircle
                  size={48}
                  style={{
                    margin: "0 auto var(--space-4)",
                    color: "var(--textTertiary)",
                  }}
                />
                <h3 style={{ marginBottom: "var(--space-2)" }}>No hay usuarios</h3>
                <p>
                  {searchTerm
                    ? "No se encontraron usuarios con ese criterio"
                    : "No hay usuarios registrados aún"}
                </p>
              </div>
            ) : (
              <div className="responsive-table">
                <Table columns={columns} data={filteredUsers} hoverable />
              </div>
            )}
          </Card>

              {showVisitSummary && (
                <div ref={visitSummaryRef}>
                  <VisitSummary />
                </div>
              )}
            </>
          ) : (
            <SystemConfig />
          )}

          {/* Los módulos que se añadieron con el roadmap. Van debajo de lo
              que ya existía: quien usa esto a diario no debería tener que
              buscar de nuevo lo que hace todos los días. */}
          <div style={{ marginTop: "var(--space-8)" }}>
            <Navegador rol="admin" />
          </div>
        </div>
      </div>
    </>
  );
}
